/**
 * #4625 — the one place that decides whether to ask somebody to rate the app,
 * and the only place that fires the prompt in a build a customer can run.
 *
 * The one other caller is a test-mode-only debug button in App Settings, which
 * calls the native wrapper directly to make the dialog visible on a device. It
 * skips the gate, the cap and the telemetry, so it is not a fifth trigger. See
 * design-docs/review-prompts.md, and the containment test in
 * `review-prompt-triggers.test.ts` that stops it moving to a customer-visible
 * surface unnoticed.
 *
 * Every trigger point calls `considerPromptingAsync`. They decide only WHEN a
 * good moment happened; who deserves one is decided here, by the pure gate in
 * `shared/src/review-prompt-gate.ts`. Keeping that split is what lets each rule
 * be tested on its own and keeps four call sites from each growing their own
 * half-copy of the conditions.
 *
 * **Nothing here shows a question of our own.** Estimating satisfaction from
 * behaviour and then firing the operating system's own prompt is precisely what
 * makes this legal: showing a "do you like the app?" question and sending only
 * the happy answers to the store is review gating, which Google punishes by
 * deleting *all* of an app's reviews. See design-docs/review-prompts.md.
 *
 * **This service is long-lived on purpose**, and that is load-bearing for the
 * weekly-checkup trigger — see `scheduleAfterCheckupPrompt`.
 */
import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import {
  decideReviewPrompt, NoPromptReason, ReviewPromptDecision, resolveReviewPromptPhase, resolveRolloutPercent,
} from '@cf-shared/review-prompt-gate';
import {
  exercisesWithRecordCandidates, personalRecordReport, PersonalRecordRow, workoutBeatAPersonalRecord,
} from '@cf-shared/review-prompt-personal-record';
import { Log_WorkoutExercise } from '@cf-shared/data-model/log-workout';
import { bucketForIdentifier } from '@cf-shared/payment-provider-bucketing';
import { isInGrace } from '@cf-shared/data-model/license-reconciler';
import { isSubscriberInFreeTrial, isSubscriberTestPurchase } from '@cf-shared/data-model/couch-user';
import { AuthService } from './auth.service';
import { ConfigDbService, ConfigKey } from './config-db.service';
import { LoggingService, errorReportFields } from './logging.service';
import { SettingsService } from './settings.service';
import { TelemetryService } from './telemetry.service';
import { UserDb } from './user-db.service';
import { didSomethingGoWrongThisSession } from './session-health';
import { canShowStoreReviewPrompt, requestAppReviewAsync } from './cs-review-prompt';
import { awaitSignalOrTimeoutAsync, FirstProbeGateOutcome } from './first-probe-gate';
import { sleep } from '@cf-shared/shared-util';

/**
 * Which moment asked. Reported with the telemetry event so the spread across
 * the four triggers is visible — the spec measured the audience per moment, and
 * a trigger that never fires is otherwise indistinguishable from one nobody
 * reaches.
 */
export enum ReviewPromptTrigger {
  WeeklyCheckup = 'weekly-checkup',
  DeloadAccepted = 'deload-accepted',
  VolumeAutoregulationAccepted = 'volume-autoregulation-accepted',
  PlateauResolutionAccepted = 'plateau-resolution-accepted',
  SettingsRow = 'settings-row',
  /** #4816, phase 2 only: a finished workout beat a personal record. */
  PersonalRecord = 'personal-record',
}

/**
 * `review_prompt_skipped` reasons that are not gate refusals (#4816). In each,
 * the gate said yes and the moment itself did not happen, so they do not belong
 * in `NoPromptReason`.
 */
enum MomentSkipReason {
  /** The gate allowed somebody at the end of a workout, but nothing in it beat a record. */
  NoPersonalRecord = 'no-personal-record',
  /** The record lookup took too long, so the moment had passed. */
  PersonalRecordLookupTooSlow = 'personal-record-lookup-too-slow',
}

/**
 * How long after the checkup page closes before the prompt fires.
 *
 * Long enough for the navigation to settle and the win to register, short
 * enough that it still reads as a consequence of finishing the checkup.
 */
const AFTER_CHECKUP_DELAY_MS = 1_500;

/**
 * How long after a workout finishes before the personal-record moment is
 * considered (#4816). Long enough for the completion card to draw and for the
 * finished workout to be saved, so the gate's own workout read counts it.
 */
const AFTER_WORKOUT_DELAY_MS = 1_500;

/**
 * How long the personal-record lookup may take before the moment counts as passed
 * (#4816). The first lookup on a device builds the `bestStrengthByExercise` view
 * from every document, which can be slow on a big account. Past this, a prompt
 * would appear long after the workout, on whatever screen the person is on by
 * then, so it is skipped. The build carries on regardless, so the next finished
 * workout finds the view ready.
 */
export const PERSONAL_RECORD_LOOKUP_TIMEOUT_MS = 10_000;

@Injectable()
export class ReviewPromptService {
  constructor(
    private auth: AuthService,
    private configDb: ConfigDbService,
    private logging: LoggingService,
    private settings: SettingsService,
    private telemetry: TelemetryService,
    private userDb: UserDb,
  ) {}

  /**
   * A qualifying moment happened. Ask the gate, and prompt if it says yes.
   *
   * **Fire and forget this — never `await` it before doing something else.**
   * It reads the workout history from the database and then calls a native
   * plugin, and on Android that plugin's promise can in principle never settle
   * (see `cs-review-prompt.ts`). It never throws, so `void`-ing it is safe.
   */
  async considerPromptingAsync(trigger: ReviewPromptTrigger): Promise<void> {
    try {
      // nothing to show on the web, so do not spend a database read working out
      // whether somebody who cannot be prompted deserves to be
      if (!canShowStoreReviewPrompt()) return;

      const decision = await this.decideAsync();
      await this.reportMalformedConfigAsync(decision);
      if (!decision.mayPrompt) {
        this.trackSkipped(trigger, decision.reason);
        return;
      }

      await this.firePromptAsync(trigger, decision);
    } catch (err) {
      // a failure to ask for a rating must never break the thing the person was
      // actually doing, and must never vanish either
      this.logging.trackSilentProblem('ReviewPromptConsiderFailed',
        { trigger, ...errorReportFields(err) });
    }
  }

  /**
   * A weekly checkup produced a result and the person finished it. Prompt once
   * the page has closed and the screen behind it has settled.
   *
   * **The timer is owned by this service, and that is the whole point.** The
   * obvious way to write this delay — `safeTimeout` on the checkup page — is
   * the one thing that guarantees it never runs: that helper exists to CANCEL a
   * pending timer when its view is destroyed, and the view being destroyed is
   * exactly the event we are waiting for. It would fail in complete silence.
   * This service is never destroyed, so a plain timer is correct here and
   * `safeTimeout` would be wrong.
   */
  scheduleAfterCheckupPrompt(): void {
    setTimeout(() => {
      void this.considerPromptingAsync(ReviewPromptTrigger.WeeklyCheckup);
    }, AFTER_CHECKUP_DELAY_MS);
  }

  /**
   * #4816 — a workout has just finished. At phase 2, prompt if it beat a
   * personal record.
   *
   * Called only when the workout CHANGES to finished, never when somebody edits
   * a set of a workout that was already finished, so an edit can never fire it.
   * Asking at the end rather than when the set concludes keeps the prompt out of
   * the middle of a workout, and fires at most once per workout however many
   * records fell.
   *
   * The sets are copied now, so a set edited during the delay is judged as it was
   * when the workout finished. The timer is this service's for the same reason as
   * `scheduleAfterCheckupPrompt`: the workout page may be gone when it runs.
   */
  scheduleAfterWorkoutPrompt(workoutId: string, exercises: Log_WorkoutExercise[]): void {
    const finishedSets = (exercises || []).map(exercise => ({
      exerciseId: exercise?.exerciseId,
      sets: (exercise?.sets || []).map(set => ({
        state: set?.state,
        setType: set?.setType,
        stats: { estimated1rm_net_kg: set?.stats?.estimated1rm_net_kg },
      })),
    })) as Log_WorkoutExercise[];
    setTimeout(() => {
      void this.considerPersonalRecordPromptAsync(workoutId, finishedSets);
    }, AFTER_WORKOUT_DELAY_MS);
  }

  /**
   * The personal-record moment itself. Never throws, like `considerPromptingAsync`.
   *
   * **The gate runs BEFORE the record is looked up**, the reverse of the other
   * moments, where the moment is known before the gate is asked. Looking a record
   * up reads the `bestStrengthByExercise` view, and the first read on a device
   * builds it from every document; asking the gate first means only somebody who
   * could be prompted ever pays that. The cost is that a refusal here is not
   * recorded as `review_prompt_skipped`: we do not know whether a record fell, so
   * the moment is not known to have happened, and recording one per finished
   * workout would bury the other moments' numbers.
   */
  private async considerPersonalRecordPromptAsync(workoutId: string, exercises: Log_WorkoutExercise[]): Promise<void> {
    const trigger = ReviewPromptTrigger.PersonalRecord;
    try {
      if (!canShowStoreReviewPrompt()) return;
      // phase 1 knows nothing of this moment: no read, no event
      if (resolveReviewPromptPhase(await this.readConfiguredPhaseAsync()).phase < 2) return;
      const exerciseIds = exercisesWithRecordCandidates(exercises);
      if (exerciseIds.length === 0) return;

      const decision = await this.decideAsync();
      await this.reportMalformedConfigAsync(decision);
      // if the gate refuses, or the phase changed under us, stay quiet (see above)
      if (!decision.mayPrompt || (decision.phase ?? 1) < 2) return;

      // a time bound, so a slow first build of the view or a hung read can
      // never leave the moment pending, or fire it long after the workout
      let bestElsewhere_net_kg: Map<string, number> | undefined;
      const lookup = this.userDb.getBestStrengthElsewhereAsync(exerciseIds, workoutId)
        .then(found => { bestElsewhere_net_kg = found; });
      const outcome = await awaitSignalOrTimeoutAsync(lookup, sleep, PERSONAL_RECORD_LOOKUP_TIMEOUT_MS);
      if (outcome === FirstProbeGateOutcome.Timeout) {
        // a lookup that fails after we stopped waiting must still be reported
        lookup.catch(err => this.logging.trackSilentProblem('ReviewPromptConsiderFailed',
          { trigger, ...errorReportFields(err) }));
        this.trackSkipped(trigger, MomentSkipReason.PersonalRecordLookupTooSlow);
        return;
      }
      if (!workoutBeatAPersonalRecord(exercises, bestElsewhere_net_kg as Map<string, number>)) {
        this.trackSkipped(trigger, MomentSkipReason.NoPersonalRecord);
        return;
      }
      // the view read can take a while on its first build; if anything went wrong
      // meanwhile, this is no longer a good moment
      if (didSomethingGoWrongThisSession()) {
        this.trackSkipped(trigger, NoPromptReason.SomethingWentWrongThisSession);
        return;
      }

      await this.firePromptAsync(trigger, decision);
    } catch (err) {
      this.logging.trackSilentProblem('ReviewPromptConsiderFailed', { trigger, ...errorReportFields(err) });
    }
  }

  /**
   * #4816 — what the personal-record moment decides for the most recently
   * finished workout, for the test-mode tool in App Settings.
   *
   * Ignores the gate, the phase and the platform on purpose: it answers only "did
   * this workout beat a record?", which is the part a browser test can reach.
   * Absent when no workout has been finished.
   */
  async explainLatestWorkoutPersonalRecordAsync(): Promise<PersonalRecordRow[] | undefined> {
    const workout = await this.userDb.getLatestFinishedWorkoutAsync();
    if (!workout) return undefined;
    const exerciseIds = exercisesWithRecordCandidates(workout.exercises);
    const bestElsewhere_net_kg = exerciseIds.length
      ? await this.userDb.getBestStrengthElsewhereAsync(exerciseIds, workout._id)
      : new Map<string, number>();
    return personalRecordReport(workout.exercises, bestElsewhere_net_kg);
  }

  /**
   * Ask the gate, gathering every input it needs.
   *
   * Exposed rather than private so a future debugger can call it from the
   * console to find out why a particular account is never asked. Nothing calls
   * it today besides `considerPromptingAsync` — it has no test of its own,
   * because this service cannot be instantiated under the node test runner.
   */
  async decideAsync(now: DateTime = DateTime.utc()): Promise<ReviewPromptDecision> {
    const couch = this.auth.couch;

    // a coach managing somebody else's account must never be asked to rate the
    // app on the strength of that person's data — and the settings sync is not
    // even installed then, so a cap marker written now would not persist
    const isCoachViewingClient = !!this.userDb.getCoachManagingClient();

    // paid-and-not-trial, computed the same way the Refiner traits already do it
    const hasPaidAccess = !!couch
      && couch.subscriptionStatus !== undefined
      && isInGrace(couch.subscriptionStatus, couch.licenseExpires_utc, now.toMillis())
      && !isSubscriberTestPurchase(couch);
    const isInFreeTrial = !!couch && isSubscriberInFreeTrial({
      appleData: couch.appleData,
      googlePlayData: couch.googlePlayData,
      paddleData: couch.paddleData,
      payProData: { trialEnds_utc: couch.trialEnds_utc },
    }, now);

    const workoutHistory = await this.userDb.getWorkoutHistoryForReviewPromptAsync(now);

    return decideReviewPrompt({
      configuredRolloutPercent: await this.readConfiguredRolloutAsync(),
      bucket: bucketForIdentifier(this.auth.user?.uid),
      isCoachViewingClient,
      somethingWentWrongThisSession: didSomethingGoWrongThisSession(),
      lastReportedAProblem: this.settings.getReviewPromptLastProblemReportedAt(),
      lastAskedToRate: this.settings.getReviewPromptLastAskedAt(),
      asksToRate: this.settings.getReviewPromptAsks(),
      configuredPhase: await this.readConfiguredPhaseAsync(),
      // `!!couch` is the right test because every subscription fact below is
      // computed from that one login result. See design-docs/review-prompts.md,
      // "The subscription facts have the same third answer".
      hasSubscriptionFacts: !!couch,
      hasPaidAccess,
      isInFreeTrial,
      // "cleanly active" is the same question as having paid access without
      // being in a trial: a suspended, paused or expired subscription already
      // fails `isInGrace`, which is an allow-list.
      isSubscriptionCleanlyActive: hasPaidAccess,
      firstPaidAt: couch?.firstPaidAt_utc ? DateTime.fromISO(couch.firstPaidAt_utc) : undefined,
      workoutHistory,
      now,
    });
  }

  /**
   * Fire the prompt and record that we asked.
   *
   * **The cap marker is written whether or not anything appeared**, because
   * neither store tells us — there is no callback and no rating. The attempt is
   * all we can count, and it is the right thing to count anyway: the cap exists
   * to stop us pestering somebody, and an ask the operating system suppressed
   * still used up the moment we chose.
   *
   * The marker is written BEFORE the request. If the two were the other way
   * round, a request that hung would leave the cap unwritten and the person
   * askable again at the next qualifying moment.
   */
  private async firePromptAsync(trigger: ReviewPromptTrigger, decision: ReviewPromptDecision): Promise<void> {
    await this.settings.recordReviewPromptAskedAsync();
    // the RESOLVED percent, not the raw configured value. They differ in exactly
    // the cases this property exists to explain: an unset or unreadable config
    // resolves to 100 (this feature fails open) while the raw value is
    // `undefined`, which Mixpanel drops — and an absent value is documented to
    // mean the Settings row, so sending it here would make the two
    // indistinguishable. A malformed value resolves to 100 too, rather than
    // being reported verbatim into a number-typed property.
    const rollout = resolveRolloutPercent(await this.readConfiguredRolloutAsync());
    this.telemetry.trackEvent('review_prompt_attempted', {
      trigger,
      rollout_percent: rollout.percent,
      // #4816: which audience was in force, and which tenure rule let them in,
      // so the two phases and the two rules can be told apart in the numbers
      phase: decision.phase,
      tenure_rule: decision.tenureRule,
    });
    await requestAppReviewAsync(this.logging);
  }

  /**
   * Record that a qualifying moment happened and we did NOT ask.
   *
   * Without this, a trigger that never fires and a trigger nobody is eligible
   * for look identical in the data — both are simply an absence. See
   * design-docs/review-prompts.md, "Telemetry", for what that already cost.
   *
   * `reason` is the gate's own named refusal. It is always present when the
   * gate refuses, but the fallback is here because the gate's package compiles
   * without `strictNullChecks`, so nothing in the type system enforces that.
   */
  private trackSkipped(trigger: ReviewPromptTrigger, reason?: NoPromptReason | MomentSkipReason): void {
    this.telemetry.trackEvent('review_prompt_skipped', {
      trigger,
      reason: reason ?? 'unknown',
    });
  }

  /** Record that the Settings row was used. It is a store link, not the prompt. */
  trackSettingsRowUsed(): void {
    this.telemetry.trackEvent('review_prompt_attempted', {
      trigger: ReviewPromptTrigger.SettingsRow,
      rollout_percent: undefined,
    });
  }

  /**
   * Report a configured value that could not be read. Reported here because the
   * gate is pure and cannot report anything itself.
   */
  private async reportMalformedConfigAsync(decision: ReviewPromptDecision): Promise<void> {
    if (decision.configMalformed) {
      // an operator who mistyped the ramp while narrowing it in an incident
      // would otherwise get a silent 100% rollout with nothing saying so
      this.logging.trackSilentProblem('ReviewPromptRolloutMalformed', {
        configured: String(await this.readConfiguredRolloutAsync()),
      });
    }
    if (decision.phaseMalformed) {
      // an operator who mistyped the phase gets phase 1, and must be told why
      // the wider audience never appeared
      this.logging.trackSilentProblem('ReviewPromptPhaseMalformed', {
        configured: String(await this.readConfiguredPhaseAsync()),
      });
    }
  }

  /** The configured phase, unvalidated — the gate decides what it means. */
  private async readConfiguredPhaseAsync(): Promise<unknown> {
    return this.configDb.getConfigValueAsync(ConfigKey.ReviewPromptPhase);
  }

  /** The configured rollout percent, unvalidated — the gate decides what it means. */
  private async readConfiguredRolloutAsync(): Promise<unknown> {
    return this.configDb.getConfigValueAsync(ConfigKey.ReviewPrompts);
  }
}
